import { FailedReason } from './constants';
import { IWindowProvider } from './event-listener.interface';
import {
  FrameContexts,
  HostClientType,
  HubNames,
  IFrameInfo,
  ILocaleInfo,
  IWebContentMessageEvent,
  IWebContentRequestMessage,
  IWebContentResponseMessage,
  Themes,
} from './messages.interface';

export interface IGalleryTab {
  configurationUrl: string;
}

export interface IStaticTab {
  contentUrl: string;
  entityId: string;
  name: string;
  websiteUrl?: string;
}

export interface IWebApplicationInfo {
  id: string;
  resource?: string;
}
export interface IAppDefinition {
  appId: string;
  galleryTabs?: IGalleryTab[];
  isFullTrustApp?: boolean;
  showTabLoadingIndicator: boolean;
  staticTabs?: IStaticTab[];
  tenantId?: string;
  validDomains?: ReadonlyArray<string>;
  webApplicationInfo?: IWebApplicationInfo;
  version: string;
}

export interface IAppManifest extends IAppDefinition {
  accentColor?: string; // App accent color
  developerName?: string; // Name to show in about page
  developerUrl?: string; // Link to devs home page
  largeImageUrl?: string; // Image to show in about page
  longDescription?: string; // Description for about page (supports markdown)
  isFullScreen?: boolean; // If app is full screen (used to show/hide header)
  name: string; // App name (shows up in the header)
  privacyUrl?: string; // Link to privacy policy
  screenshotUrls?: string[]; // List of screenshots for about page
  shortDescription?: string; // Blurb for about page
  showLoadingIndicator?: boolean; // Show loading ui while tabs are loading or not
  smallImageUrl?: string; // Icon for the header
  termsOfUseUrl?: string; // Link to ToS page
  videoUrl?: string; // Video for about page
}

/**
 * Interface for Hosted web content sdk service
 */
export interface IHostedWebContentAuthSdkService {
  /**
   * is authorization window open
   */
  isWindowOpen(): boolean;

  /**
   * to send request message to auth frame
   * @param functionName: function name
   * @param funArguments: function arguments
   */
  sendRequestMessage(func: string, args?: (object | string)[]): void;

  /**
   * close authorization window
   */
  closeWindow(): void;
}
/**
 * Interface for Hosted web content sdk service
 */
export interface IHostedWebContentSdkService {
  /**
   * initialize frame and load target origin to frame
   * @param frame: target frame
   * @param origin: target origin
   * @param appDefinition: Hosted web content app definition
   */
  setFrameAndLoadOrigin(
    frame: IFrameInfo | undefined | null,
    origin: string,
    appDefinition: IAppDefinition,
    userClickTime: number
  ): Promise<void>;

  /**
   * to send request message to frame
   * @param functionName: function name
   * @param args: function arguments
   */
  sendRequestMessage(functionName: string, args?: (object | string)[]): void;
}

export interface IMessageEventWithAsyncResponseHandler extends MessageEvent {
  /**
   * send async response to app
   */
  sendAsyncResponse?: (sdkRequestMessage: IWebContentRequestMessage) => void;
}

/**
 * Communication bridge to facilitate message passing between Hub SDK used by the host
 * and App SDK used by the hosted application.
 */
export interface ICommunicationBridge {
  /**
   * Called by SDK on initialization
   * @param hostWindow window of the Host
   */
  onSdkInitialized?: (hostWindow: Window | IWindowProvider) => void;

  /**
   * Called by SDK whenever the App url is changed
   * @param frame Frame where app should be loaded. It abstracts common functionalities required by frame types and hence, is expected to be used for iFrames, Electron WebView, WebView2 control
   * @param origin Content url of the launch page of the app which is being loaded
   * @param appDefinition AppDefinition of the app
   * @param handleMessageCallback Callback which handles the messages from Apps. Messages received from apps should be routed to this callback for Hubs to handle it
   */
  onSetFrameAndLoadOrigin: (
    frame: IFrameInfo,
    origin: string,
    appDefinition: IAppDefinition,
    handleMessageCallback: (event: IWebContentMessageEvent) => void
  ) => void;

  /**
   * Called by SDK to post message to the App Window
   * @param target Frame where app should be loaded. It abstracts common functionalities required by frame types and hence, is expected to be used for iFrames, Electron WebView, WebView2 control
   * @param message Message to be sent to the app
   */
  postMessage: (target: IFrameInfo, message: IWebContentRequestMessage | IWebContentResponseMessage) => void;

  /**
   * Called by SDK to dispose the resources used by communication bridge
   */
  dispose: () => void;
}

export type AppInitInfo =
  | { state: AppInitState.Unknown | AppInitState.Loading | AppInitState.Loaded }
  | { state: AppInitState.Failed; reason: FailedReason };

/**
 * Used to track the app initialization state of the MetaOS App hosted
 */
export enum AppInitState {
  Unknown = 'unknown',

  /**
   * Set when beginning the loading process, before setting the iframe URL
   */
  Loading = 'loading',

  /**
   * Used to hide the Hub loading UI
   * IF the native loading indicator is being used, set in response to appInitialization.appLoaded and appInitialization.success
   * Otherwise, set immediately after creating the iFrame
   */
  Loaded = 'loaded',

  /**
   * Used to show the Hub error UI
   * IF the native loading indicator is being used, set in response to appInitialization.failure
   * or if the appInitialization.success wasn't received after 30s
   * Otherwise, set if no Initialize message received after 60s
   */
  Failed = 'failed',
}

export interface SdkError {
  /**
  error code
  */
  errorCode: ErrorCode;
  /**
  Optional description for the error. This may contain useful information for web-app developers.
  This string will not be localized and is not for end-user consumption.
  App should not depend on the string content. The exact value may change. This is only for debugging purposes.
  */
  message?: string;
}

export enum ErrorCode {
  /**
   * API not supported in the current platform.
   */
  NOT_SUPPORTED_ON_PLATFORM = 100,
  /**
   * Internal error encountered while performing the required operation.
   */
  INTERNAL_ERROR = 500,
  /**
  Permissions denied by user
  */
  PERMISSION_DENIED = 1000,
  /**
   * Network issue
   */
  NETWORK_ERROR = 2000,
  /**
   * Underlying hardware doesn't support the capability
   */
  NO_HW_SUPPORT = 3000,
  /**
   * One or more arguments are invalid
   */
  INVALID_ARGUMENTS = 4000,
  /**
   * User is not authorized for this operation
   */
  UNAUTHORIZED_USER_OPERATION = 5000,
  /**
   * Could not complete the operation due to insufficient resources
   */
  INSUFFICIENT_RESOURCES = 6000,
  /**
   * Platform throttled the request because of API was invoked too frequently
   */
  THROTTLE = 7000,
  /**
   * User aborted the operation
   */
  USER_ABORT = 8000,
  /**
   * Could not complete the operation in the given time interval
   */
  OPERATION_TIMED_OUT = 8001,
  /**
   * Platform code is old and doesn't implement this API
   */
  OLD_PLATFORM = 9000,
  /**
   * The file specified was not found on the given location
   */
  FILE_NOT_FOUND = 404,
  /**
   * The return value is too big and has exceeded our size boundaries
   */
  SIZE_EXCEEDED = 10000,
}

/**
 * Indicates the team type, currently used to distinguish between different team
 * types in Office 365 for Education (team types 1, 2, 3, and 4).
 */
export enum TeamType {
  Standard = 0,
  Edu = 1,
  Class = 2,
  Plc = 3,
  Staff = 4,
}

/**
 * The type of the channel with which the content is associated.
 */
export enum ChannelType {
  Regular = 'Regular',
  Private = 'Private',
  Shared = 'Shared',
}

/**
 * Indicates the various types of roles of a user in a team.
 */
export enum UserTeamRole {
  Admin = 0,
  User = 1,
  Guest = 2,
}

/**
 * Allowed user file open preferences
 */
export enum FileOpenPreference {
  Inline = 'inline',
  Desktop = 'desktop',
  Web = 'web',
}

export interface ContextBridge {
  /**
   * The Office 365 group ID for the team with which the content is associated.
   * This field is available only when the identity permission is requested in the manifest.
   */
  groupId?: string;

  /**
   * The Microsoft Teams ID for the team with which the content is associated.
   */
  teamId?: string;

  /**
   * The name for the team with which the content is associated.
   */
  teamName?: string;

  /**
   * The Microsoft Teams ID for the channel with which the content is associated.
   */
  channelId?: string;

  /**
   * The name for the channel with which the content is associated.
   */
  channelName?: string;

  /**
   * The type of the channel with which the content is associated.
   */
  channelType?: ChannelType;

  /**
   * The developer-defined unique ID for the entity this content points to.
   */
  entityId: string;

  /**
   * The developer-defined unique ID for the sub-entity this content points to.
   * This field should be used to restore to a specific state within an entity,
   * such as scrolling to or activating a specific piece of content.
   */
  subEntityId?: string;

  /**
   * The current locale that the user has configured for the app formatted as
   * languageId-countryId (for example, en-us).
   */
  locale: string;

  /**
   * More detailed locale info from the user's OS if available. Can be used together with
   * the @microsoft/globe NPM package to ensure your app respects the user's OS date and
   * time format configuration
   */
  osLocaleInfo?: ILocaleInfo;

  /**
   * @deprecated Use loginHint or userPrincipalName.
   * The UPN of the current user.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  upn?: string;

  /**
   * The Azure AD tenant ID of the current user.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  tid?: string;

  /**
   * The current UI theme.
   */
  theme?: string;

  /**
   * Indication whether the tab is in full-screen mode.
   */
  isFullScreen?: boolean;

  /**
   * The type of the team.
   */
  teamType?: TeamType;

  /**
   * The root SharePoint site associated with the team.
   */
  teamSiteUrl?: string;

  /**
   * The domain of the root SharePoint site associated with the team.
   */
  teamSiteDomain?: string;

  /**
   * The relative path to the SharePoint site associated with the team.
   */
  teamSitePath?: string;

  /**
   * The tenant ID of the host team.
   */
  hostTeamTenantId?: string;

  /**
   * The AAD group ID of the host team.
   */
  hostTeamGroupId?: string;

  /**
   * The relative path to the SharePoint folder associated with the channel.
   */
  channelRelativeUrl?: string;

  /**
   * Unique ID for the current Teams session for use in correlating telemetry data.
   */
  sessionId?: string;

  /**
   * The user's role in the team.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to the user's role, and never as proof of her role.
   */
  userTeamRole?: UserTeamRole;

  /**
   * The Microsoft Teams ID for the chat with which the content is associated.
   */
  chatId?: string;

  /**
   * A value suitable for use as a login_hint when authenticating with Azure AD.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  loginHint?: string;

  /**
   * The UPN of the current user. This may be an externally-authenticated UPN (e.g., guest users).
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  userPrincipalName?: string;

  /**
   * The Azure AD object id of the current user.
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  userObjectId?: string;

  /**
   * Indicates whether team is archived.
   * Apps should use this as a signal to prevent any changes to content associated with archived teams.
   */
  isTeamArchived?: boolean;

  /**
   * The name of the host client. Possible values are: Office, Orange, Outlook, Teams
   */
  hostName?: HubNames;

  /**
   * The type of the host client. Possible values are : android, ios, web, desktop, rigel
   */
  hostClientType?: HostClientType;

  /**
   * The context where tab url is loaded (content, task, setting, remove, sidePanel)
   */
  frameContext?: FrameContexts;

  /**
   * SharePoint context. This is only available when hosted in SharePoint.
   */
  sharepoint?: any;

  /**
   * The type of license for the current users tenant.
   */
  tenantSKU?: string;

  /**
   * The license type for the current user.
   */
  userLicenseType?: string;

  /**
   * The ID of the parent message from which this task module was launched.
   * This is only available in task modules launched from bot cards.
   */
  parentMessageId?: string;

  /**
   * Current ring ID
   */
  ringId?: string;

  /**
   * Unique ID for the current session for use in correlating telemetry data.
   */
  appSessionId?: string;

  /**
   * Represents whether calling is allowed for the current logged in User
   */
  isCallingAllowed?: boolean;

  /**
   * Represents whether PSTN calling is allowed for the current logged in User
   */
  isPSTNCallingAllowed?: boolean;

  /**
   * Meeting Id used by tab when running in meeting context
   */
  meetingId?: string;

  /**
   * The OneNote section ID that is linked to the channel.
   */
  defaultOneNoteSectionId?: string;

  /**
   * Indication whether the tab is in a pop out window
   */
  isMultiWindow?: boolean;

  /**
   * Personal app icon y coordinate position
   */
  appIconPosition?: number;

  /**
   * Source origin from where the tab is opened
   */
  sourceOrigin?: string;

  /**
   * Time when the user clicked on the tab
   */
  userClickTime?: number;

  /**
   * Team Template ID if there was a Team Template associated with the creation of the team.
   */
  teamTemplateId?: string;

  /**
   * Where the user prefers the file to be opened from by default during file open
   */
  userFileOpenPreference?: FileOpenPreference;

  /**
   * The address book name of the current user.
   */
  userDisplayName?: string;
}

/**
 * Type to ingest context related information from Hubs.
 */
export interface IWebContentContext extends BaseContext {
  /**
   * User specific details
   */
  user?: UserDetailsInfo;
}

/**
 * Type of getContext struct in AppSDK.
 * It is used for placeholding context information
 */
export interface AppContext extends BaseContext {
  /**
   * Info of the user
   */
  user?: UserInfo;
}

export type UserInfo = UserProfile & UserDetailsInfo;

export interface BaseContext {
  /**
   * Info of the app
   */
  app: AppInfo;

  /**
   * Info of the current page of App
   */
  page: PageInfo;

  /**
   * Info of the Microsoft Teams channel
   */
  channel?: ChannelInfo;

  /**
   * Info of the Microsoft Teams chat
   */
  chat?: ChatInfo;

  /**
   * Info of the Microsoft Teams meeting
   */
  meeting?: MeetingInfo;

  /**
   * SharePoint context. This is only available when hosted in SharePoint.
   */
  sharepoint?: any;

  /**
   * Info of the sharePoint site associated with the team.
   */
  sharePointSite?: SharePointSiteInfo;

  /**
   * Info of the Microsoft Teams team
   */
  team?: TeamInfo;
}

export interface AppInfo {
  /**
   * The current locale that the user has configured for the app formatted as
   * languageId-countryId (for example, en-us).
   */
  locale: string;

  /**
   * The current UI theme.
   */
  theme: Themes;

  /**
   * Unique ID for the current session for use in correlating telemetry data.
   */
  sessionId: string;

  /**
   * Info of the host
   */
  host: AppHostInfo;

  /**
   * More detailed locale info from the user's OS if available. Can be used together with
   * the @microsoft/globe NPM package to ensure your app respects the user's OS date and
   * time format configuration
   */
  osLocaleInfo?: ILocaleInfo;
  /**
   * Personal app icon y coordinate position
   */
  iconPositionVertical?: number;

  /**
   * Time when the user clicked on the tab
   */
  userClickTime?: number;

  /**
   * The ID of the parent message from which this task module was launched.
   * This is only available in task modules launched from bot cards.
   */
  parentMessageId?: string;

  /**
   * Where the user prefers the file to be opened from by default during file open
   */
  userFileOpenPreference?: FileOpenPreference;
}

export interface AppHostInfo {
  /**
   * The name of the host client. Possible values are: Office, Orange, Outlook, Teams
   */
  name: HubNames;

  /**
   * The type of the host client. Possible values are : android, ios, web, desktop, rigel
   */
  clientType: HostClientType;

  /**
   * Unique ID for the current Host session for use in correlating telemetry data.
   */
  sessionId: string;

  /**
   * Current ring ID
   */
  ringId?: string;

  /**
   * Hub version
   */
  version: string;
}

export interface ChannelInfo {
  /**
   * The Microsoft Teams ID for the channel with which the content is associated.
   */
  id: string;

  /**
   * The name for the channel with which the content is associated.
   */
  displayName?: string;

  /**
   * The relative path to the SharePoint folder associated with the channel.
   */
  relativeUrl?: string;

  /**
   * The type of the channel with which the content is associated.
   */
  membershipType?: ChannelType;

  /**
   * The OneNote section ID that is linked to the channel.
   */
  defaultOneNoteSectionId?: string;

  /**
   * The tenant ID of the team which owns the channel.
   */
  ownerTenantId?: string;

  /**
   * The AAD group ID of the team which owns the channel.
   */
  ownerGroupId?: string;
}

export interface ChatInfo {
  /**
   * The Microsoft Teams ID for the chat with which the content is associated.
   */
  id: string;
}

export interface MeetingInfo {
  /**
   * Meeting Id used by tab when running in meeting context
   */
  id: string;
}

export interface PageInfo {
  /**
   * The developer-defined unique ID for the page this content points to.
   */
  id: string;

  /**
   * The context where page url is loaded (content, task, setting, remove, sidePanel)
   */
  frameContext: FrameContexts;

  /**
   * The developer-defined unique ID for the sub-page this content points to.
   * This field should be used to restore to a specific state within a page,
   * such as scrolling to or activating a specific piece of content.
   */
  subPageId?: string;

  /**
   * Indication whether the page is in full-screen mode.
   */
  isFullScreen?: boolean;

  /**
   * Indication whether the page is in a pop out window
   */
  isMultiWindow?: boolean;

  /**
   * Source origin from where the page is opened
   */
  sourceOrigin?: string;
}

export interface TeamInfo {
  /**
   * The Microsoft Teams ID for the team with which the content is associated.
   */
  internalId: string;

  /**
   * The name for the team with which the content is associated.
   */
  displayName?: string;

  /**
   * The type of the team.
   */
  type?: TeamType;

  /**
   * The Office 365 group ID for the team with which the content is associated.
   * This field is available only when the identity permission is requested in the manifest.
   */
  groupId?: string;

  /**
   * Indicates whether team is archived.
   * Apps should use this as a signal to prevent any changes to content associated with archived teams.
   */
  isArchived?: boolean;

  /**
   * Team Template ID if there was a Team Template associated with the creation of the team.
   */
  templateId?: string;

  /**
   * The user's role in the team.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to the user's role, and never as proof of her role.
   */
  userRole?: UserTeamRole;
}

export interface UserProfile {
  /**
   * The Azure AD object id of the current user.
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  id: string;

  /**
   * The address book name of the current user.
   */
  displayName?: string;

  /**
   * A value suitable for use as a login_hint when authenticating with Azure AD.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  loginHint?: string;

  /**
   * The UPN of the current user. This may be an externally-authenticated UPN (e.g., guest users).
   * Because a malicious party run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  userPrincipalName?: string;
}

export interface UserDetailsInfo {
  /**
   * Represents whether calling is allowed for the current logged in User
   */
  isCallingAllowed?: boolean;

  /**
   * Represents whether PSTN calling is allowed for the current logged in User
   */
  isPSTNCallingAllowed?: boolean;

  /**
   * The license type for the current user.
   */
  licenseType?: string;

  /**
   * The tenant related info of the current user.
   */
  tenant?: TenantInfo;
}

export interface TenantInfo {
  /**
   * The Azure AD tenant ID of the current user.
   * Because a malicious party can run your content in a browser, this value should
   * be used only as a hint as to who the user is and never as proof of identity.
   * This field is available only when the identity permission is requested in the manifest.
   */
  id: string;

  /**
   * The type of license for the current users tenant.
   */
  sku?: string;
}

export interface SharePointSiteInfo {
  /**
   * The root SharePoint site associated with the team.
   */
  url?: string;

  /**
   * The domain of the root SharePoint site associated with the team.
   */
  domain?: string;

  /**
   * The relative path to the SharePoint site associated with the team.
   */
  path?: string;
}
